
import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { Product } from '@/types/common';
import OptimizedPremiumProductCard from './OptimizedPremiumProductCard';
import VirtualizedProductGrid from './VirtualizedProductGrid';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export type ViewDensity = 'comfortable' | 'compact' | 'dense';

interface LazyProductGridProps {
  products?: Product[];
  density?: ViewDensity;
  useVirtualization?: boolean;
  virtualizationThreshold?: number;
  itemsPerPage?: number;
}

const LazyProductGrid = memo<LazyProductGridProps>(({ 
  products = [], 
  density = 'comfortable',
  useVirtualization = true,
  virtualizationThreshold = 50,
  itemsPerPage = 20
}) => {
  const [displayedItems, setDisplayedItems] = useState(itemsPerPage);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Memoized grid classes based on density
  const gridClasses = useMemo(() => {
    switch (density) {
      case 'comfortable':
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6";
      case 'compact':
        return "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4";
      case 'dense':
        return "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3";
      default:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6";
    }
  }, [density]);

  // Memoized visible products
  const visibleProducts = useMemo(() => {
    return products.slice(0, displayedItems);
  }, [products, displayedItems]);

  const loadMore = useCallback(async () => {
    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    setDisplayedItems(prev => Math.min(prev + itemsPerPage, products.length));
    setIsLoadingMore(false);
  }, [itemsPerPage, products.length]);

  const hasMore = displayedItems < products.length;

  // Reset displayed items when products change
  useEffect(() => {
    setDisplayedItems(itemsPerPage);
  }, [products, itemsPerPage]);

  // Use virtualization for large datasets
  if (useVirtualization && products.length > virtualizationThreshold) {
    const itemsPerRow = density === 'dense' ? 6 : density === 'compact' ? 5 : 4;
    return <VirtualizedProductGrid products={products} itemsPerRow={itemsPerRow} />;
  }

  // Regular grid with lazy loading
  return (
    <div className="space-y-8">
      <div className={gridClasses}>
        {visibleProducts.map((product) => (
          <OptimizedPremiumProductCard key={product.id} product={product} />
        ))}
      </div>
      
      {hasMore && (
        <div className="flex justify-center">
          <Button
            onClick={loadMore}
            disabled={isLoadingMore}
            variant="outline"
            size="lg"
            className="px-8 py-3"
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              `Load More (${products.length - displayedItems} remaining)`
            )}
          </Button>
        </div>
      )}
    </div>
  );
});

LazyProductGrid.displayName = 'LazyProductGrid';

export default LazyProductGrid;
